import type { BlockDef } from '@cogo/types-v2'
import type { ChannelOutput, PageProgress, DispatchTwoOpts } from '../_shared/channel-types'
import type { ResolvedExample } from './mock-corpus-picker'

export interface MockStreamArgs {
  examples: ResolvedExample[]
  shapeSha: string
  totalDurationMs: number
  pageCount: number
  chunkIntervalMs: number
  mockMode: NonNullable<DispatchTwoOpts['mockMode']>
  signal?: AbortSignal
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const id = setTimeout(() => resolve(), ms)
    if (signal) {
      const onAbort = () => {
        clearTimeout(id)
        resolve()
      }
      if (signal.aborted) onAbort()
      else signal.addEventListener('abort', onAbort, { once: true })
    }
  })

export async function* mockStream(args: MockStreamArgs): AsyncIterable<ChannelOutput> {
  const { examples, shapeSha, totalDurationMs, pageCount, chunkIntervalMs, mockMode, signal } = args

  if (signal?.aborted) {
    yield { kind: 'miss', reason: 'aborted' }
    return
  }

  if (mockMode === 'miss') {
    yield { kind: 'miss', reason: 'no-candidates' }
    return
  }

  // success / shaclFail / timeout — all start streaming
  const pageDurationMs = totalDurationMs / pageCount
  const chunksPerPage = Math.max(1, Math.floor(pageDurationMs / chunkIntervalMs))
  const totalChunks = chunksPerPage * pageCount

  const buildSnapshot = (chunkIndex: number, elapsedMs: number): ChannelOutput => {
    // When chunkIndex >= totalChunks all pages are done (final all-done snapshot)
    const allDone = chunkIndex >= totalChunks
    const currentPage = allDone
      ? pageCount  // sentinel: all pages < pageCount are 'done'
      : Math.min(Math.floor(chunkIndex / chunksPerPage), pageCount - 1)
    const intraPage = allDone ? 0 : chunkIndex % chunksPerPage
    const pages: PageProgress[] = examples.map((ex, i) => {
      if (allDone || i < currentPage) return { id: ex.pageId, name: ex.pageName, status: 'done' }
      if (i === currentPage) {
        const progress = Math.min(intraPage / chunksPerPage, 1)
        return { id: ex.pageId, name: ex.pageName, status: 'in-progress', progress }
      }
      return { id: ex.pageId, name: ex.pageName, status: 'pending' }
    })
    return {
      kind: 'streaming',
      pages,
      elapsedMs,
      totalDurationEstimateMs: totalDurationMs,
    }
  }

  const startMs = (typeof performance !== 'undefined' ? performance.now() : Date.now())
  let cumulativeMs = 0

  for (let chunk = 0; chunk < totalChunks; chunk++) {
    if (signal?.aborted) {
      yield { kind: 'miss', reason: 'aborted' }
      return
    }
    yield buildSnapshot(chunk, cumulativeMs)
    await sleep(chunkIntervalMs, signal)
    if (signal?.aborted) {
      yield { kind: 'miss', reason: 'aborted' }
      return
    }
    cumulativeMs += chunkIntervalMs

    // shaclFail at midpoint (after page 1 completes, into page 2)
    if (mockMode === 'shaclFail' && chunk === Math.floor(chunksPerPage * 2.5) - 1) {
      yield {
        kind: 'shaclFail',
        failedPageId: examples[2]?.pageId,
        diagnostics: [
          `mock SHACL violation: cogo:requiredField missing on ${examples[2]?.pageName ?? 'unknown'}`,
        ],
        partialResult: {
          manifest: synthesizeManifest(examples.slice(0, 2)),
          blocks: synthesizeBlocks(examples.slice(0, 2)),
        },
      }
      return
    }

    // timeout at first-page completion — wait for abort
    if (mockMode === 'timeout' && chunk === chunksPerPage - 1) {
      await new Promise<void>((resolve) => {
        if (signal) signal.addEventListener('abort', () => resolve(), { once: true })
      })
      yield { kind: 'miss', reason: 'aborted' }
      return
    }
  }

  // Final snapshot — all pages done
  const allDoneSnapshot = buildSnapshot(totalChunks, totalDurationMs)
  yield allDoneSnapshot
  await sleep(0, signal)

  const generationMs =
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startMs
  const totalBindMs = examples.reduce((acc, _) => acc, 0)

  yield {
    kind: 'rendered',
    channel: 2,
    manifest: synthesizeManifest(examples),
    blocks: synthesizeBlocks(examples),
    bindWarnings: examples.flatMap(e => e.bindWarnings),
    timings: { lookupMs: 0, bindMs: totalBindMs, generationMs, totalMs: generationMs },
    shapeSha,
  }
}

// Synthesize a canonical App manifest. AppV2 (`AppRuntime`) requires
// `pages[i]: { id, route, layout, blocks, blockLayout }` — `blockRefs` is
// not the field name (mirror A3.1 channel-1 dispatch.ts:139, lesson F-1).
// `layout: 'stack'` avoids `[syncNav]` console noise (lesson F-5).
// `route` per page is derived from pageId; first page is '/' so it matches
// AppRuntime's default `_currentRoute = pages[0]?.route ?? '/'`.
function synthesizeManifest(examples: ResolvedExample[]): never {
  return {
    rootBlockId: examples[0]?.id,
    pages: examples.map((e, i) => ({
      id: e.pageId,
      route: i === 0 ? '/' : `/${e.pageId}`,
      layout: 'stack',
      blocks: [e.id],
      blockLayout: {},
    })),
  } as never
}

// Synthesize the canonical block bundle. AppV2 reads `block.ui.root`
// (`enrichBlock(name)?.ui?.root` at app-runtime.ts:519) — bare UiElement
// returned by bindExample is not enough (mirror A3.1 channel-1
// dispatch.ts:158, lesson F-1).
function synthesizeBlocks(examples: ResolvedExample[]): Record<string, BlockDef> {
  const out: Record<string, BlockDef> = {}
  for (const e of examples) {
    out[e.id] = {
      meta: { id: e.id, name: e.id } as never,
      ui: { root: e.boundBlock } as never,
      events: {} as never,
      flow: {} as never,
      state: {} as never,
      data: {} as never,
    } as BlockDef
  }
  return out
}
